// Address query ↔ common view state — the mockup core.js rules (parseQuery · syncUrl · periodToQuery · validPeriod):
//   lang, theme, scope, period (today|7d|30d|90d|YYYY-MM-DD~YYYY-MM-DD, alias period=custom&from=…&to=…), method (cli|api,
//   absent = all), team, person. Screen-specific keys (item, tab, page, q …) are kept as they are.
// A link opened by someone else is re-judged with that person's permissions (scope/team fixed in useView), and values that
// break a rule open as the default with a notice ("주소의 조건 일부를 바꿔 열었어요").
import { DAY, isCustomPeriod, parseYmd, ymd, type CustomPeriod, type Period } from '../lib/time';

export type Lang = 'ko' | 'en';
export type Theme = 'light' | 'dark' | 'system';
export type Method = 'all' | 'cli' | 'api';
export type ViewScope = 'me' | 'team' | 'all';

export const STATE_KEYS = ['lang', 'theme', 'scope', 'period', 'method', 'team', 'person'] as const;
export type StateKey = (typeof STATE_KEYS)[number];

/** Ordered key → value pairs (later duplicates win), '+' read as a space — like the mockup parseQuery. */
export function parseQuery(str: string): Record<string, string> {
  const o: Record<string, string> = {};
  String(str || '')
    .replace(/^\?/, '')
    .split('&')
    .forEach((p) => {
      if (!p) return;
      const i = p.indexOf('=');
      try {
        const k = decodeURIComponent(i < 0 ? p : p.slice(0, i));
        o[k] = i < 0 ? '' : decodeURIComponent(p.slice(i + 1).replace(/\+/g, ' '));
      } catch {
        /* malformed escape: skip that pair */
      }
    });
  return o;
}

/** Query string from pairs (empty values dropped, '~' kept readable): '?a=1&period=2026-10-01~2026-10-20'. */
export function buildQuery(q: Record<string, string | null | undefined>): string {
  const qs = Object.keys(q)
    .filter((k) => q[k] !== '' && q[k] != null)
    .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(String(q[k])).replace(/%7E/gi, '~'))
    .join('&');
  return qs ? '?' + qs : '';
}

export const periodToQuery = (p: Period | null | undefined): string => (isCustomPeriod(p as Period) ? `${(p as CustomPeriod).from}~${(p as CustomPeriod).to}` : (p as string) || '7d');

/** A picked range: valid dates, start ≤ end, not in the future, at most 366 days. */
export function validCustom(from: string | undefined, to: string | undefined, now: number = Date.now()): CustomPeriod | null {
  const re = /^\d{4}-\d{2}-\d{2}$/;
  if (!from || !to || !re.test(from) || !re.test(to) || from > to) return null;
  if (to > ymd(now)) return null;
  if ((parseYmd(to) - parseYmd(from)) / DAY > 365) return null;
  return { from, to };
}

export function validPeriod(p: string | undefined, q?: Record<string, string>, now: number = Date.now()): Period | null {
  if (p === 'today' || p === '7d' || p === '30d' || p === '90d') return p;
  if (p === 'custom') return validCustom(q?.from, q?.to, now);
  const m = /^(\d{4}-\d{2}-\d{2})~(\d{4}-\d{2}-\d{2})$/.exec(p || '');
  return m ? validCustom(m[1], m[2], now) : null;
}

export const validMethod = (m: string | undefined): Method => (m === 'cli' || m === 'api' ? m : 'all');
export const validScope = (s: string | undefined): ViewScope | null => (s === 'me' || s === 'team' || s === 'all' ? s : null);
export const validLang = (l: string | undefined): Lang | null => (l === 'ko' || l === 'en' ? l : null);
export const validTheme = (t: string | undefined): Theme | null => (t === 'light' || t === 'dark' || t === 'system' ? t : null);
/** Ids are opaque server strings: letters, digits, '_' '-' '.' ':' (no spaces or slashes). */
export const validId = (v: string | undefined): string | null => (v && /^[A-Za-z0-9_.:-]{1,80}$/.test(v) ? v : null);

/** A wording key + parameters, translated when shown (the language may change after the URL was read). */
export interface UrlNote {
  key: string;
  params?: Record<string, unknown>;
}

export interface UrlState {
  lang?: Lang;
  theme?: Theme;
  /** What the link asked for (re-judged with the reader's permissions). */
  scope?: ViewScope;
  period?: Period;
  method?: Method;
  team?: string;
  person?: string;
  /** Keys that were present but invalid (the period/method fallbacks need a notice). */
  invalid: StateKey[];
}

/** Read the common state keys from a query string. Absent keys stay undefined (= keep the current state). */
export function readUrlState(search: string, now: number = Date.now()): UrlState {
  const q = parseQuery(search);
  const out: UrlState = { invalid: [] };
  if ('lang' in q) (validLang(q.lang) ? (out.lang = validLang(q.lang)!) : out.invalid.push('lang'));
  if ('theme' in q) (validTheme(q.theme) ? (out.theme = validTheme(q.theme)!) : out.invalid.push('theme'));
  if ('scope' in q) (validScope(q.scope) ? (out.scope = validScope(q.scope)!) : out.invalid.push('scope'));
  if ('period' in q) {
    const p = validPeriod(q.period, q, now);
    if (p) out.period = p;
    else out.invalid.push('period');
  }
  if ('method' in q) {
    out.method = validMethod(q.method);
    if (q.method && q.method !== 'all' && out.method === 'all') out.invalid.push('method');
  }
  if ('team' in q) (validId(q.team) ? (out.team = validId(q.team)!) : out.invalid.push('team'));
  if ('person' in q) (validId(q.person) ? (out.person = validId(q.person)!) : out.invalid.push('person'));
  return out;
}

export interface WritableState {
  lang: Lang;
  theme: Theme;
  scope: ViewScope;
  period: Period;
  method: Method;
  team: string | null;
  person: string | null;
}

/**
 * The mockup syncUrl: keep the other keys, always write lang/theme/scope/period, write method/team/person only when set.
 * The alias form period=custom&from=…&to=… is folded into period=from~to.
 */
export function writeUrlState(search: string, st: WritableState): string {
  const q = parseQuery(search);
  if (q.period === 'custom') {
    delete q.from;
    delete q.to;
  }
  q.lang = st.lang;
  q.theme = st.theme;
  q.scope = st.scope;
  q.period = periodToQuery(st.period);
  if (st.method && st.method !== 'all') q.method = st.method;
  else delete q.method;
  if (st.team) q.team = st.team;
  else delete q.team;
  if (st.person) q.person = st.person;
  else delete q.person;
  return buildQuery(q);
}
